// GM Print — Service page content (SEO-focused for Brisbane keywords)
// Each page targets a distinct keyword cluster and includes an expertise article.

export const SERVICES = [
  {
    slug: "business-cards-brisbane",
    tileTitle: "Business Cards",
    tileDescription: "First impressions, done right. Premium stocks, foils, embosses.",
    heading: "Business Cards, Brisbane.",
    subheading: "Premium finishes, sharp turnaround, and prices that make sense — sourced and matched to the right press for your job.",
    seoTitle: "Business Card Printing Brisbane | Premium Finishes | GM Print",
    seoDescription: "Business card printing in Brisbane. Premium stocks, foils, spot UV, letterpress and same-day options — produced by our Brisbane team. Quote in 24 hours.",
    keywords: "business card printing Brisbane, premium business cards Brisbane, embossed business cards, foil business cards Brisbane",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-03-15",
    readTime: "5 min read",
    heroImage: null,
    heroImageAlt: "Premium printed business cards laid on textured surface for a Brisbane client",
    articleTitle: "The Business Card Is Not Dead — It's Just Grown Up.",
    articleBody: [
      "Every time someone announces the death of the business card, another Brisbane executive fishes one out of a wallet to close a deal. In 15+ years watching brands grow, I've never seen a card fail to land — provided the card is worth landing.",
      "The mistake most Brisbane businesses make is treating cards like a commodity. They aren't. A card is a 55×90mm sample of your brand's craft. It's held, turned over, run between fingers. If the paper is thin and the print is muddy, that's exactly what your prospect assumes your service will be.",
      "The good news: doing it right in Brisbane costs less than most people think. A 400gsm uncoated stock with soft-touch laminate and a subtle spot UV logo will price under a dollar a card in reasonable quantities — and it will outperform a designer $5 template every single time.",
    ],
    articleSections: [
      {
        title: "Stocks that actually feel like something",
        body: "The default 350gsm gloss is where most Brisbane cards die. Try 400gsm uncoated, 450gsm cotton, or triplex sandwich stocks with a coloured seam. Every one of these is produced by our print team at prices that reflect the job.",
      },
      {
        title: "Finishes worth paying for",
        body: "Foil stamping (metallic or holographic), letterpress deboss, spot UV on matte lamination, and painted edges are the four finishes that consistently earn their keep. Skip embossing unless the design was drawn for it — done poorly it cheapens the whole card.",
      },
      {
        title: "The Brisbane quantity sweet spot",
        body: "Most SMB Brisbane clients over-order (2,000 cards that go stale) or under-order (100 cards that don't hit unit-cost economy). For a network professional, 500 is usually right. For a sales team, order per person, not per company.",
      },
      {
        title: "What we do differently",
        body: "As Brisbane's one-stop print, design & marketing service, we handle every stage — artwork, proofing, production and delivery. We match your brief to the right press for the job, then manage it end-to-end so you don't have to.",
      },
    ],
    relatedServices: ["signage-brisbane", "publications-brisbane", "promotional-items-brisbane"],
  },
  {
    slug: "signage-brisbane",
    tileTitle: "Signage",
    tileDescription: "Corflute, pull-ups, A-frames, acrylics—indoor and outdoor that lasts in the QLD sun.",
    heading: "Signage, Brisbane.",
    subheading: "From shopfront to event, corflute to acrylic — signage that holds up in the Queensland sun and looks sharp doing it.",
    seoTitle: "Signage Brisbane | Shopfront, Event & Outdoor Signs | GM Print",
    seoDescription: "Signage printing Brisbane — corflute, pull-up banners, A-frames, acrylic, shopfront and event signs. UV-stable, Queensland sun-tested. Free quote in 24 hours.",
    keywords: "signage Brisbane, sign printing Brisbane, shopfront signs Brisbane, event signage Brisbane, corflute Brisbane",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-03-18",
    readTime: "6 min read",
    heroImage: null,
    heroImageAlt: "Stadium sideline signage boards at a sports ground in Queensland",
    articleTitle: "Brisbane Signage: What Survives Summer, What Doesn't.",
    articleBody: [
      "Brisbane is not kind to signage. Between 35°C summers, cyclonic afternoon storms and UV that fades cheap vinyl in a month, the signs you see peeling on Milton Road weren't badly designed — they were badly specified.",
      "Signage is one of the few print categories where knowing the material matters as much as knowing the design. A gorgeous logo on the wrong substrate looks tired within a season. A pragmatic layout on the right stock looks sharp for years. In 15+ years of print marketing, I've learned the substrate almost always wins the argument.",
      "Below is a practical breakdown of the signage we produce most for Brisbane clients — and where each type genuinely earns its keep.",
    ],
    articleSections: [
      {
        title: "Corflute — the workhorse",
        body: "5mm corflute with UV-stable digital print holds up 6-9 months outdoor in Brisbane conditions. Great for real estate, election, event, or short-term shopfront use. Cheap, quick, replaceable — but not for anything you want to look premium.",
      },
      {
        title: "Pull-up banners & A-frames",
        body: "Standard fabric pull-ups (85×200cm) suit indoor events. For outdoor Brisbane markets, spec a weighted-base A-frame with printed inserts you can swap. Metal frames beat plastic every time — the sun bakes plastic hinges brittle inside a year.",
      },
      {
        title: "Acrylic & 3D lettering",
        body: "Laser-cut 3-5mm acrylic with vinyl overlay is the sweet spot for premium reception signage. Add a 25mm standoff for depth and shadow. For shopfronts, brushed metal or push-through acrylic with LED backlighting reads clean day or night.",
      },
      {
        title: "The UV rating that actually matters",
        body: "Ask any Brisbane sign supplier about ink UV rating and you'll get a shrug. The one to specify is 3-year outdoor cast vinyl (like Avery MPI 1105) with a laminate overlay. Non-laminated prints fade faster than most clients expect.",
      },
      {
        title: "Why our signage costs less",
        body: "Because we manage every stage in-house and match each job to the right press, we deliver premium signage at prices that reflect the actual production — not layered mark-ups.",
      },
    ],
    relatedServices: ["banners-brisbane", "business-cards-brisbane", "marketing-campaigns-brisbane"],
  },
  {
    slug: "banners-brisbane",
    tileTitle: "Banners",
    tileDescription: "Vinyl, mesh, fabric. Any size, any event, any deadline.",
    heading: "Banners, Brisbane.",
    subheading: "Vinyl, mesh, fabric — produced by our Brisbane print & media team for events, sport, retail and construction.",
    seoTitle: "Banner Printing Brisbane | Vinyl, Mesh & Fabric Banners | GM Print",
    seoDescription: "Banner printing Brisbane — vinyl, mesh, fabric, PVC and event banners. Any size, any deadline. Free quote in 24 hours from Brisbane's one-stop print, design & marketing service.",
    keywords: "banner printing Brisbane, vinyl banners Brisbane, mesh banners Brisbane, event banners Brisbane, PVC banner printing",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-03-22",
    readTime: "4 min read",
    heroImage: null,
    heroImageAlt: "Outdoor event banners printed and hanging at a Brisbane festival",
    articleTitle: "Big Print, Small Details — The Banner Playbook.",
    articleBody: [
      "A banner is the most-photographed piece of print your Brisbane business will ever produce. Every sponsor logo at every Sunday game, every backdrop at every gala, every corflute at every fun run — the banner is the print job that people literally stand next to and pose for.",
      "Yet banners are the print category most Brisbane clients under-invest in. A saggy PVC banner with drooping corners next to a $1,200 catered function is the fastest way to make a $50,000 event look cheap.",
      "Getting banners right isn't about spending more — it's about specifying smarter.",
    ],
    articleSections: [
      {
        title: "Substrate: match the job",
        body: "13oz PVC vinyl is the default and works for most events. For coastal or high-wind Brisbane venues (looking at you, Bulimba Riverside), specify wind-cut mesh — it flexes instead of tearing. Fabric banners (dye-sub polyester) beat vinyl indoors — no glare in event photography.",
      },
      {
        title: "The finishing that stops the wobble",
        body: "Hem all four sides. Grommet every 500mm. For anything over 3m, spec pole pockets top and bottom. This is the difference between a banner that reads professional and one that looks apologetic in every photo.",
      },
      {
        title: "Colour, calibrated",
        body: "Wide-format inkjet doesn't match your business card. If brand colour precision matters, spec Pantone spot-colour matching (built into every job we produce) or supply CMYK values from a soft-proof — don't send a screen-optimised PDF and hope.",
      },
      {
        title: "What we manage for you",
        body: "Artwork prep, proofing, production, sizing, freight to venue and (where required) install coordination. One point of contact from brief to bump-out.",
      },
    ],
    relatedServices: ["signage-brisbane", "publications-brisbane", "promotional-items-brisbane"],
  },
  {
    slug: "publications-brisbane",
    tileTitle: "Publications",
    tileDescription: "Yearbooks, prospectuses, magazines, newsletters, programs.",
    heading: "Publications, Brisbane.",
    subheading: "Yearbooks, prospectuses, magazines, tournament programs — produced with the paper, binding and finish your Brisbane audience deserves.",
    seoTitle: "Publication Printing Brisbane | Yearbooks, Prospectuses & Magazines | GM Print",
    seoDescription: "Publication printing Brisbane — school yearbooks, prospectuses, magazines, programs and newsletters. Perfect-bound, saddle-stitched, wire-bound. Quote in 24 hours.",
    keywords: "publication printing Brisbane, yearbook printing Brisbane, prospectus printing Brisbane, magazine printing Brisbane, program printing Brisbane",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-04-02",
    readTime: "7 min read",
    heroImage: null,
    heroImageAlt: "Stack of printed magazines and publications on a shelf",
    articleTitle: "Why Brisbane Schools Still Print (And Print Well).",
    articleBody: [
      "In a decade of working with Brisbane schools, sports clubs and country clubs, one thing has held true: the printed piece is the one that gets kept. Parents throw away URLs. They keep the yearbook. Members ignore emails. They read the tournament program.",
      "Publications are the print category where the paper, binding and finish do half the work of your design. Get those right and the content lands harder. Get them wrong and even the best photography looks amateur.",
      "Here's what we produce most, and how we brief them for consistent quality.",
    ],
    articleSections: [
      {
        title: "Yearbooks: the stock choice matters",
        body: "For Christian schools we produce for in Brisbane, the go-to is 128gsm silk internals with a 300gsm gloss laminated cover. It photographs beautifully, resists teenage rough handling, and prices sensibly at 300+ copies. Upgrade the cover to matte laminate + spot UV for premium schools.",
      },
      {
        title: "Prospectuses: front-of-brand real estate",
        body: "This is the document a prospective parent takes home. Spec 170gsm silk internals minimum, hard-cover casebound or heavy soft-cover with French flaps. If your school competes on prestige, this is not where to save $200.",
      },
      {
        title: "Tournament programs & member directories",
        body: "Saddle-stitched (stapled) for anything under 48 pages, perfect-bound above. Uncoated stock for a premium editorial feel, silk for photo-heavy pieces. We produce directories for Brisbane golf clubs where paper choice alone lifted member response rates.",
      },
      {
        title: "Turnaround expectations",
        body: "Yearbooks: 3-4 weeks from final PDF. Prospectuses: 2 weeks. Programs: 5-7 days. Rush jobs are possible but come with premiums — plan backwards from your event date.",
      },
    ],
    relatedServices: ["business-cards-brisbane", "signage-brisbane", "marketing-campaigns-brisbane"],
  },
  {
    slug: "promotional-items-brisbane",
    tileTitle: "Promotional Items",
    tileDescription: "Merchandise that gets remembered. Full range, sourced smart.",
    heading: "Promotional Items, Brisbane.",
    subheading: "Merchandise that earns wallet space, desk space, or gym-bag space — sourced smart, branded well, delivered to your Brisbane door.",
    seoTitle: "Promotional Products Brisbane | Branded Merchandise | GM Print",
    seoDescription: "Promotional products Brisbane — branded merchandise, corporate gifts, trade show giveaways, uniforms. Full range sourced through Brisbane's one-stop print, design & marketing service.",
    keywords: "promotional products Brisbane, branded merchandise Brisbane, corporate gifts Brisbane, trade show giveaways Brisbane",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-04-10",
    readTime: "5 min read",
    heroImage: null,
    heroImageAlt: "Branded canvas tote bag promotional merchandise",
    articleTitle: "The Merch That Actually Gets Used.",
    articleBody: [
      "The average Brisbane conference attendee walks away with 8-12 branded items and uses maybe two of them. In 15+ years of running promotion campaigns, I've watched clients spend $8,000 on cheap keychains that end up in a bin at the airport.",
      "Promotional merchandise is a keeping game, not a giving game. If it doesn't earn a permanent spot in someone's daily rotation — wallet, desk, gym bag, car — it may as well not exist.",
      "Here's what we produce for Brisbane clients that actually gets used.",
    ],
    articleSections: [
      {
        title: "The four categories worth spending on",
        body: "1) Quality drinkware (branded Frank Green or similar). 2) Cotton canvas totes (heavier than 200gsm). 3) Notebooks with a premium cover. 4) Branded apparel one tier above what people expect. Everything else is filler.",
      },
      {
        title: "The trap: cheap plastic pens",
        body: "The industry's most-ordered item. Also the most-binned. If you must give away pens, spec a metal-body option (Parker Jotter engraved) at 5x the price and 1/10th the quantity. Better recall, better ROI.",
      },
      {
        title: "Print vs embroidery on apparel",
        body: "Screen print for volume (100+), DTG for photo-quality small runs, embroidery for polos and caps (best perceived value). We match Brisbane suppliers to the right method for your budget and quantity.",
      },
      {
        title: "Lead time reality",
        body: "Branded merch is a 3-4 week category, minimum. If your Brisbane event is in two weeks, brace for domestic-decorated stock (higher unit cost) or rethink the item. We'll tell you honestly what's possible.",
      },
    ],
    relatedServices: ["business-cards-brisbane", "signage-brisbane", "marketing-campaigns-brisbane"],
  },
  {
    slug: "marketing-campaigns-brisbane",
    tileTitle: "Marketing Campaigns",
    tileDescription: "Digital + print campaigns for small businesses that actually convert.",
    heading: "Marketing Campaigns, Brisbane.",
    subheading: "Digital + print marketing campaigns for Brisbane small businesses — strategic, integrated, and built to convert.",
    seoTitle: "Marketing Agency Brisbane | Digital & Print Campaigns | GM Print",
    seoDescription: "Marketing campaigns Brisbane — integrated digital and print for small businesses. 15+ years growing brands. Strategy, creative, and print — one service.",
    keywords: "marketing agency Brisbane, marketing campaigns Brisbane, small business marketing Brisbane, print marketing Brisbane, digital marketing Brisbane",
    author: "Sarah Morris",
    authorRole: "Brand & Marketing Director",
    publishedDate: "2026-04-18",
    readTime: "8 min read",
    heroImage: null,
    heroImageAlt: "Marketing team collaborating on a Brisbane campaign with printed reports",
    articleTitle: "Small Business Marketing in Brisbane — What Works in 2026.",
    articleBody: [
      "I've spent 15+ years as a Brand and Marketing Director helping Brisbane small businesses grow through strategic marketing and promotion. The playbook has changed, but the fundamentals haven't: the businesses that win are the ones that tell a consistent story across every channel — print, digital, and everything in between.",
      "The Brisbane SMB marketing landscape in 2026 rewards specificity. Broad, generic campaigns are being drowned out by hyper-targeted, well-crafted, integrated ones. Below are the four moves I recommend to every Brisbane business we brief.",
    ],
    articleSections: [
      {
        title: "1. Nail your brand toolkit first",
        body: "Before you spend a dollar on advertising, tighten your brand — logo, palette, typography, tone of voice, and templates for the five pieces you'll produce most. Business card, one-pager, flyer, social tile, email header. That toolkit lasts 3-5 years and pays back everywhere.",
      },
      {
        title: "2. Integrated campaigns beat platform-specific ones",
        body: "The Brisbane brands growing fastest right now are running the same message across print (letterbox drops, event signage) and digital (Meta ads, EDMs) simultaneously. Same visual identity, same headline, same call to action. Frequency beats novelty.",
      },
      {
        title: "3. Local relevance is a compounding advantage",
        body: "Reference Brisbane suburbs, Queensland weather, local events. National generic messaging is a race to the bottom. \"Doing print in Toowong\" beats \"servicing all of Australia\" for Brisbane conversion.",
      },
      {
        title: "4. Measure the print, not just the pixels",
        body: "Use unique landing pages, QR codes, or dedicated phone numbers on your print pieces. If you're spending on flyers or signage, you should know exactly how many leads each piece generates. We build this measurement into every campaign we brief.",
      },
      {
        title: "How we work with Brisbane small businesses",
        body: "Strategy session first (one hour, no cost, no obligation). We map your quarter, your channels, and your production needs. Then we execute — creative, print production, digital deployment — all through one point of contact.",
      },
    ],
    relatedServices: ["business-cards-brisbane", "publications-brisbane", "promotional-items-brisbane"],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
